import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import janineBorisImage from "@/assets/janine-boris-interview.jpg";

interface InterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InterviewDialog = ({ open, onOpenChange }: InterviewDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="text-2xl font-bold text-primary">
            Interview mit den Gründern von RentePlusImmobilie
          </DialogTitle>
          <DialogDescription className="text-lg text-muted-foreground">
            Warum ist die Immobilienverrentung so komplex und gleichzeitig so wichtig und beratungsintensiv?
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="px-6 pb-6 max-h-[70vh]">
          <div className="space-y-6">
            {/* Founders Image */}
            <div className="flex justify-center mb-6">
              <img 
                src={janineBorisImage}
                alt="Boris Hardi und Janine Hardi, Gründer von RentePlusImmobilie"
                className="rounded-lg shadow-lg max-w-md w-full h-auto"
              />
            </div>

            {/* Interview Content */}
            <div className="space-y-6 text-sm leading-relaxed">
              <div className="space-y-4">
                <p className="font-semibold text-accent">
                  <strong>Interviewer:</strong> Herr Hardi, Frau Hardi – Sie beschäftigen sich seit Jahren mit der Immobilienverrentung. Warum ist dieses Thema überhaupt so wichtig?
                </p>
                <p>
                  <strong className="text-primary">Boris Hardi:</strong> Weil wir über ein riesiges gesellschaftliches Thema sprechen. Mehr als fünf Millionen Senioren in Deutschland besitzen eine schuldenfreie Immobilie, gleichzeitig reicht die Rente bei vielen nicht für den Alltag. Die Nebenkosten steigen, Pflege- und Gesundheitsausgaben nehmen zu. Die Immobilie ist also das größte Vermögen – aber sie bringt im Ruhestand oft kein laufendes Einkommen. Hier setzt die Verrentung an: Sie macht das in der Immobilie gebundene Kapital nutzbar, ohne dass die Eigentümer ausziehen müssen.
                </p>
              </div>

              <div className="space-y-4">
                <p className="font-semibold text-accent">
                  <strong>Interviewer:</strong> Klingt eigentlich einfach. Warum sagen Sie, dass es so komplex ist?
                </p>
                <p>
                  <strong className="text-primary">Janine Hardi (Rechtsanwältin):</strong> Weil jede Verrentung rechtlich, steuerlich und familiär anders ist. Wir reden über unterschiedliche Modelle wie Leibrente, Teilverkauf, Nießbrauch, Wohnrecht, Rückmiete oder auch Kreditvarianten. Jedes Modell hat eigene Konsequenzen – für die Eigentümer selbst, für deren Erben, aber auch für die Sicherheit im Grundbuch. Ein falscher Vertrag oder eine unklare Regelung kann sehr teuer werden.
                </p>
                <p>
                  <strong className="text-primary">Boris Hardi:</strong> Dazu kommt: Banken vergeben ab einem gewissen Alter keine klassischen Darlehen mehr, die EU-Wohnimmobilienkreditrichtlinie macht es Senioren sehr schwer. Deshalb braucht es maßgeschneiderte Lösungen.
                </p>
              </div>

              <div className="space-y-4">
                <p className="font-semibold text-accent">
                  <strong>Interviewer:</strong> Heißt das, ohne Beratung geht es kaum?
                </p>
                <p>
                  <strong className="text-primary">Janine Hardi:</strong> Genau. Die Verrentung ist keine Standardfinanzierung. Sie berührt immer mehrere Ebenen:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Rechtlich (z. B. Absicherung des Wohnrechts oder Nießbrauchs im Grundbuch)</li>
                  <li>Finanziell (Wie viel Kapital oder monatliche Rente ist realistisch?)</li>
                  <li>Familiär (Wie wirken sich die Entscheidungen auf die Erben aus?)</li>
                </ul>
                <p>Darum ist es absolut beratungsintensiv.</p>
                <p>
                  <strong className="text-primary">Boris Hardi:</strong> Und es ist auch ein sehr emotionales Thema. Für viele Senioren geht es um ihr Zuhause, oft seit Jahrzehnten. Da braucht es viel Vertrauen, Zeit und Erklärung.
                </p>
              </div>

              <div className="space-y-4">
                <p className="font-semibold text-accent">
                  <strong>Interviewer:</strong> Wie unterstützen Sie Senioren konkret?
                </p>
                <p>
                  <strong className="text-primary">Boris Hardi:</strong> Wir haben mit eigenheimverrentung.de einen digitalen Berater entwickelt, der Senioren Schritt für Schritt durch die Optionen führt. Mit einfachen Fragen können sie herausfinden, welche Modelle grundsätzlich passen. Wer möchte, kann dann eine persönliche Beratung anfordern.
                </p>
                <p>
                  <strong className="text-primary">Janine Hardi:</strong> Uns ist wichtig: Keine Lösung von der Stange, sondern ein individuelles Konzept, das rechtlich sicher ist und auch den Familienfrieden berücksichtigt.
                </p>
              </div>

              <div className="space-y-4">
                <p className="font-semibold text-accent">
                  <strong>Interviewer:</strong> Abschließend gefragt: Warum lohnt es sich, über Immobilienverrentung nachzudenken?
                </p>
                <p>
                  <strong className="text-primary">Boris Hardi:</strong> Weil es Senioren die Freiheit gibt, ihr Vermögen im Alter sinnvoll zu nutzen, ohne ihr Zuhause aufgeben zu müssen. Das schafft Lebensqualität und Sicherheit.
                </p>
                <p>
                  <strong className="text-primary">Janine Hardi:</strong> Und weil es nur mit Transparenz, rechtlicher Klarheit und guter Beratung gelingt. Darum setzen wir uns täglich dafür ein.
                </p>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};